import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Airplane, X, MapPin, ArrowRight } from '@phosphor-icons/react';
import { useAuth } from '@/contexts/AuthContext';
import { daysUntil } from './TripCard';

/**
 * Shows a green banner across Dashboard / Calendar when a trip has travel_mode_active.
 * Tap to jump to that trip's detail page. Dismissible per-session via localStorage.
 */
export default function TravelModeBanner({ context = 'dashboard' }) {
  const { api } = useAuth();
  const navigate = useNavigate();
  const [trip, setTrip] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dismissedId, setDismissedId] = useState(() => {
    try { return sessionStorage.getItem('travel-banner-dismissed-id') || ''; } catch { return ''; }
  });

  const fetchActive = useCallback(async () => {
    try {
      const r = await api.get('/trips/active/travel-mode');
      setTrip(r.data?.trip || null);
    } catch (_) {
      setTrip(null);
    }
    setLoading(false);
  }, [api]);

  useEffect(() => { fetchActive(); }, [fetchActive]);

  if (loading || !trip || trip.trip_id === dismissedId) return null;

  const endDate = trip.end_date || '';
  const back = endDate ? daysUntil(endDate) : null;
  let returnLabel = '';
  if (back === null) returnLabel = '';
  else if (back < 0) returnLabel = `Trip ended ${Math.abs(back)}d ago`;
  else if (back === 0) returnLabel = 'Back today';
  else if (back === 1) returnLabel = 'Back tomorrow';
  else returnLabel = `Back in ${back}d`;

  return (
    <div
      data-testid={`travel-mode-banner-${context}`}
      className="rounded-2xl p-3 sm:p-4 mb-4 flex items-center gap-3 transition-all"
      style={{
        background: 'linear-gradient(135deg, rgba(34,197,94,0.14), rgba(16,185,129,0.06))',
        border: '1px solid rgba(34,197,94,0.40)',
      }}
    >
      <div
        className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
        style={{ background: 'rgba(34,197,94,0.25)' }}
      >
        <Airplane size={20} weight="fill" className="text-emerald-200" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <p className="text-[10px] uppercase tracking-widest font-extrabold text-emerald-300">Travel mode ON</p>
          {returnLabel && (
            <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-emerald-500/20 text-emerald-200">
              {returnLabel}
            </span>
          )}
        </div>
        <p className="text-sm font-bold text-white mt-0.5 truncate">{trip.title}</p>
        <p className="text-[11px] text-emerald-100/80 flex items-center gap-1 truncate">
          <MapPin size={11} weight="fill" />
          {trip.destination}
          {trip.start_date && trip.end_date && <span className="ml-1">· {trip.start_date} → {trip.end_date}</span>}
        </p>
      </div>
      <button
        onClick={() => navigate('/trips')}
        data-testid={`travel-banner-open-${context}`}
        className="hidden sm:inline-flex items-center gap-1 px-3 py-2 rounded-lg bg-emerald-500 text-white text-[11px] font-bold hover:bg-emerald-400"
      >
        Open trip <ArrowRight size={11} weight="bold" />
      </button>
      <button
        onClick={() => navigate('/trips')}
        data-testid={`travel-banner-open-mobile-${context}`}
        className="sm:hidden p-2 rounded-lg bg-emerald-500 text-white"
      >
        <ArrowRight size={14} weight="bold" />
      </button>
      <button
        onClick={() => {
          try { sessionStorage.setItem('travel-banner-dismissed-id', trip.trip_id); } catch {}
          setDismissedId(trip.trip_id);
        }}
        data-testid={`travel-banner-dismiss-${context}`}
        title="Hide for this session"
        className="p-1.5 rounded-lg text-emerald-200/70 hover:text-white hover:bg-white/5 transition-colors flex-shrink-0"
      >
        <X size={14} weight="bold" />
      </button>
    </div>
  );
}
